import {
  DEFAULT_TABLE_HEADER_BACKGROUND_COLOR,
  DEFAULT_TABLE_HEADER_TEXT_COLOR,
  GROUP_COLUMN_PREFIX,
  NULL_GROUPING,
} from '../Constants';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import toString from 'lodash/toString';
import union from 'lodash/union';
import { type GridApi, type CellClassParams, type ColDef, type IRowNode } from 'ag-grid-community';
import { type CustomAgGridContext } from 'common/types/agGrid/context';
import { EXPORT_TIME_FORMATS } from 'common/DataTypeFormatter';
import { type HeaderFormatStyle, type TableColumnFormat } from '../types';
import { type RowStripeStyle } from 'common/types/agGrid/rowStripe';
import { SoQLType } from 'common/types/soql';
import { type ViewColumn } from 'common/types/viewColumn';
import { defaultRowStripeValue } from 'common/authoring_workflow/components/panes/PresentationPane/RowStripeSelector';
import { generateDecimalFormat, generateIntegerFormat } from './xlsxExportCellStylesHelper';
import {
  getConfigClassIndex,
  isWithSubtotalLabel,
  shouldApplyDateFormattingClass,
  getNumberPrecisionInfo,
} from './xlsxExportCellClassesHelper';
import {
  isColumnStylingInTablesEnabled,
  isTableAdvancedTotalingEnabled,
  isTablePrintingImprovementFormattingEnabled,
} from 'common/visualizations/helpers/VifSelectors';
import { DIV_ZERO_SENTINEL } from 'common/authoring_workflow/components/modals/AdvancedTotalingModal/helpers/formulaHelpers';
import { CALENDAR_DATE_COLUMN_TYPE, NUMBER_COLUMN_TYPE } from 'common/authoring_workflow/constants';
import { getFieldName } from './getFieldName';
import { isColDefPivoted } from './isColDefPivoted';
import { isNormalCell, isSubtotalCell, isTotalCell } from './isTotalCell';
import { I18n } from 'common/i18n';

export interface IExcelStylesParams {
  id: string;
  alignment?: { horizontal: string; wrapText?: boolean };
  font?: { color?: string; italic?: boolean; bold?: boolean };
  interior?: { color: string; pattern: string };
  dataType?: string;
  numberFormat?: { format: string };
}

interface ITableExportFormattingParams {
  columnFormats: { [key: string]: TableColumnFormat };
  rowStripeStyle: RowStripeStyle | undefined;
  columnMetadata: ViewColumn[];
  headerFormat: HeaderFormatStyle;
}

/**  excelStyles in gridOptions expects an array of style objects, each of which should include
 * an id property (which corresponds to the className) that matches the values generated by generateCellClasses.
 * @returns ```
 * [
 *   {
 *       id: "style-option-<fieldname>-",
 *       font: { color: "#e0ffc1"},
 *       interior: {
 *          color: "#008000", pattern: 'Solid'
 *       }
 *   }
 * ]```;
 */
export const generateExcelStyles = (params: ITableExportFormattingParams) => {
  const { headerFormat, columnMetadata, rowStripeStyle, columnFormats } = params;
  let excelStyles: IExcelStylesParams[] = generateCommonCellClasses(headerFormat);
  excelStyles = [
    ...generateDefaultStyle(columnMetadata),
    ...excelStyles,
    ...generateRowStriping(rowStripeStyle),
  ];

  // Retrieve all field names with column formatting and overview formatting
  // Currently, only number & date types are retrieved for overview formatting
  const supportedViewColumnTypes = [CALENDAR_DATE_COLUMN_TYPE, NUMBER_COLUMN_TYPE];
  const overviewFormattingFieldnames = columnMetadata
    .filter(
      (column) => !isEmpty(column.format ?? {}) && supportedViewColumnTypes.includes(column.dataTypeName),
    )
    .map((column) => column.fieldName);

  union(overviewFormattingFieldnames, Object.keys(columnFormats)).forEach((colKey) => {
    if (columnFormats[colKey]?.style) {
      excelStyles.push(generateStyleOption(colKey, styleAttributes(columnFormats[colKey]), 'style-option'));
    }

    if (columnFormats[colKey]?.isAlignHeader) {
      const alignment = columnFormats[colKey].format?.align ?? '';
      excelStyles.push(generateHeaderAlignment(alignment, headerFormat));
    }

    const viewFormat = columnFormats[colKey]?.format?.view;
    const viewColumn = columnMetadata.find((column) => column.fieldName == colKey);
    const columnMetadataViewFormat = viewColumn?.format?.view;
    if (hasDateViewFormat(columnFormats[colKey], viewColumn)) {
      const view = isUndefined(viewFormat) ? columnMetadataViewFormat : viewFormat;
      const timeFormat = EXPORT_TIME_FORMATS[view as keyof typeof EXPORT_TIME_FORMATS];

      excelStyles.push({
        id: `datetime-formatted-cell-${colKey}`,
        dataType: 'DateTime',
        numberFormat: {
          format: timeFormat,
        },
      });
    }

    if (viewColumn?.dataTypeName === SoQLType.SoQLNumberT) {
      const styleFormat = generateIntegerFormat(columnFormats[colKey]?.format, viewColumn.format);
      excelStyles.push({
        id: `number-formatted-cell-${colKey}`,
        dataType: 'Number',
        numberFormat: {
          format: styleFormat,
        },
      });
      excelStyles = [...excelStyles, ...generateDecimalFormat(styleFormat, colKey)];
    }

    const conditionalConfigs = columnFormats[colKey]?.conditionalConfig;
    conditionalConfigs?.forEach((config, index) => {
      if (index > 0) {
        const isSameCondition = isEqual(conditionalConfigs[index - 1].expression, config.expression);
        if (isSameCondition) {
          return;
        }
      }
      excelStyles.push(generateStyleOption(colKey, styleAttributes(config), 'conditional-config', index));
    });
  });

  // Divide-by-zero error cell
  excelStyles.push({
    id: 'div-zero-error',
    font: {
      bold: true,
      color: '#ff0000',
    },
  });

  return excelStyles;
};

const hasDateViewFormat = (columnFormat: TableColumnFormat, columnViewFormat: ViewColumn | undefined) => {
  const viewFormat = columnFormat?.format?.view;
  const overviewFormat = columnViewFormat?.format?.view;

  return (
    (columnFormat?.expression?.function === 'timeRange' &&
      columnFormat?.format?.hasOwnProperty('view') &&
      !isNil(viewFormat)) ||
    !isNil(overviewFormat)
  );
};

interface IPushColumnStyleClasses {
  params: ICellClassesParams;
  configClassIndex: number | undefined;
  columnName: string;
  styleClasses: string[];
  columnFormatItem: TableColumnFormat;
}

const pushColumnStyleClasses = ({
  configClassIndex,
  params,
  styleClasses,
  columnName,
  columnFormatItem,
}: IPushColumnStyleClasses) => {
  if (!isUndefined(configClassIndex) && configClassIndex !== -1 && !isSubtotalCell(params)) {
    styleClasses.push(`conditional-config-${columnName}-${configClassIndex}`);
  }
  if (columnFormatItem && params.value && isNormalCell(params)) {
    styleClasses.push(`style-option-${columnName}`);
  }
};

export interface ICellClassesParams {
  colFieldName: string;
  colDataTypeName: string;
  node: IRowNode;
  context: CustomAgGridContext;
  value: string | number;
  colDef?: ColDef<any, any>;
  // true when the server-computed subtotal for this footer cell is the div-zero sentinel.
  // the raw value on footer rows is an ag-grid aggregate (not the sentinel), so we need this
  // side-channel to suppress the numeric cell style and let the "Error" string render.
  isDivZeroSubtotal?: boolean;
  // Grid API for accessing displayed row count
  api?: GridApi;
}

/**
 * `cellClass` expects an array of class names that match the ID values generated by `generateExcelStyles()`.
 * These class names are dependent on the `columnFormatting` values.
 *
 * For conditional formatting with multiple conditions, only one class should be returned—the one with the highest priority.
 *
 * For non-conditional formatting classes, there might be instances of multiple classes depending on the field type.
 *
 * Below is a simple example of the set of classes a cell may have.
 * @returns
 * ```
 * [
 *    'conditional-config-<fieldname>-<matchedConfigIndex>',
 *    'style-option-<fieldname>',
 *    'default-style-<fieldname>'
 * ];
 * ```
 */
export const generateCellClasses = (params: ICellClassesParams) => {
  const { colFieldName, context, node, colDataTypeName, value, colDef, isDivZeroSubtotal, api } = params;
  const styleClasses: string[] = [];
  const columnName = colFieldName.replace(`${GROUP_COLUMN_PREFIX}-`, '');
  const columnFormatItem = context.columnFormats[columnName];
  const configClassIndex = getConfigClassIndex(params, columnName);
  const isGroupedRowAndGroupedColumn = node.group && columnName === node.field;
  const isNonGroupedRowAndNonGroupedColumn = !node.group && !context.groupedColumns.includes(columnName);

  const isDivZeroError =
    isTableAdvancedTotalingEnabled() && (value == DIV_ZERO_SENTINEL || !!isDivZeroSubtotal);

  // pushed first so row stripe, conditional config, and column styles can override
  if (isDivZeroError) {
    styleClasses.push('div-zero-error');
  }

  if (isGroupedRowAndGroupedColumn || isNonGroupedRowAndNonGroupedColumn || isColDefPivoted(colDef)) {
    pushColumnStyleClasses({
      configClassIndex,
      columnFormatItem,
      columnName,
      params,
      styleClasses,
    });
  }
  if (shouldApplyDateFormattingClass(params, columnName)) {
    styleClasses.push(`datetime-formatted-cell-${columnName}`);
  }

  if (
    colDataTypeName === SoQLType.SoQLNumberT &&
    !isDivZeroError &&
    value !== NULL_GROUPING &&
    !isWithSubtotalLabel(params) &&
    !(
      isTotalCell(params) &&
      [
        I18n.t('shared.visualizations.charts.table.total'),
        context.hierarchyConfig?.customLabels?.totals?.trim(),
      ].includes(toString(value))
    )
  ) {
    const { isDecimal, decimalPlaces } = getNumberPrecisionInfo(params);
    if (isDecimal && decimalPlaces) {
      styleClasses.push(`decimal-${decimalPlaces}-formatted-cell-${columnName}`);
    } else {
      styleClasses.push(`number-formatted-cell-${columnName}`);
    }
  }

  styleClasses.push(`default-style-${columnName}`);

  let rowStripeStyle: string;

  if (isTablePrintingImprovementFormattingEnabled() && isTotalCell(params)) {
    // For total rows (pinned), use the displayed row count from export grid
    // Since export grid has all groups expanded, this gives accurate count including subtotals/grouping
    const totalDataRows = api?.getDisplayedRowCount();
    if (totalDataRows && totalDataRows > 0) {
      rowStripeStyle = totalDataRows % 2 === 0 ? 'base-row' : 'alternate-row';
    } else {
      // If API isn't ready yet, skip striping for this render - it will re-render when ready
      return styleClasses;
    }
  } else {
    // For regular rows (data, subtotals, grouping), use rowIndex
    rowStripeStyle = node.rowIndex && node.rowIndex % 2 !== 0 ? 'alternate-row' : 'base-row';
  }

  styleClasses.push(rowStripeStyle);

  return styleClasses;
};

export const tableExportRenderer = (colDef: ColDef<any, any> | undefined, rendererParams: any) => {
  return typeof colDef?.cellRenderer === 'string'
    ? colDef?.cellRendererParams.innerRenderer(rendererParams)
    : colDef?.cellRenderer(rendererParams);
};

const generateRowStriping = (rowStripeStyle: RowStripeStyle | undefined) => {
  if (!isNil(rowStripeStyle) && !isEmpty(rowStripeStyle)) {
    return ['base', 'alternate'].map((type) => {
      const stripeStyle = rowStripeStyle[type as keyof RowStripeStyle];
      const defaultStyle = defaultRowStripeValue[type as keyof RowStripeStyle];
      const style = {
        id: `${type}-row`,
        font: {
          color: stripeStyle?.text || '#000',
        },
      };

      if (!isEqual(stripeStyle?.fill, defaultStyle?.fill)) {
        const interiorStyle = {
          interior: {
            color: stripeStyle?.fill || '#fff',
            pattern: 'Solid',
          },
        };
        return { ...style, ...interiorStyle };
      } else {
        return style;
      }
    });
  }
  return [];
};

const generateDefaultStyle = (viewColumns: ViewColumn[]) => {
  return viewColumns.map((viewColumn: ViewColumn) => {
    // NOTE: For future XLSX alignment improvements, consider using getDefaultColumnAlignment()
    // from common/visualizations/views/agGridReact/helpers/TableColumnAlignment.ts, which provides
    // the same logic for TableColumn types.
    let alignment = 'Left';
    if (viewColumn.renderTypeName === SoQLType.SoQLNumberT) {
      alignment = 'Right';
    } else if ([SoQLType.SoQLBooleanT, SoQLType.SoQLBooleanAltT].includes(viewColumn.renderTypeName)) {
      alignment = 'Center';
    } else if (viewColumn.renderTypeName === SoQLType.SoQLFloatingTimestampT) {
      return {
        id: `datetime-formatted-cell-${viewColumn.fieldName}`,
        dataType: 'DateTime',
        numberFormat: {
          format: EXPORT_TIME_FORMATS.default,
        },
        alignment: {
          horizontal: alignment,
        },
      };
    }

    return {
      id: `default-style-${viewColumn.fieldName}`,
      alignment: {
        horizontal: alignment,
      },
    };
  });
};

const generateStyleOption = (colKey: string, styles: any, prefix: string, index?: number) => {
  const id = typeof index === 'number' ? `${prefix}-${colKey}-${index}` : `${prefix}-${colKey}`;
  const baseStyleOption = {
    id: id,
  };
  const stylingOption = {
    font: {
      color: styles.fontColor,
      bold: styles.bold,
      italic: styles.italic,
    },
    interior: {
      color: styles.backgroundColor,
      pattern: 'Solid',
    },
  };

  if (prefix === 'style-option') {
    return isColumnStylingInTablesEnabled() ? { ...baseStyleOption, ...stylingOption } : baseStyleOption;
  }

  return { ...baseStyleOption, ...stylingOption };
};

const styleAttributes = (formatObj: any) => {
  return {
    fontColor: formatObj.style?.textColor,
    backgroundColor: formatObj.style?.backgroundColor,
    bold: formatObj.style?.fontStyle?.isBold,
    italic: formatObj.style?.fontStyle?.isItalic,
  };
};

const capitalize = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

/** headerClass in columnDef returns an array of class names - ag-grid-header included.
 * We need to use the class name - 'ag-grid-header' to match the value from columnDef headerClass
 * we don't need multiple class names since the style is same for all column headers.
 * returns a value with type IExcelStylesParams
 * sample returned value: ```
 * { id: 'ag-grid-header', font: { color: <value>, bold: <value>, italic: <value>}, interior: { color: <value>, pattern: 'Solid' }} ```;
 */
const generateHeaderStyle = (headerFormat: HeaderFormatStyle) => {
  const isBoldHeaderFontStyle = headerFormat?.fontStyle?.isBold ?? true;
  const isItalicHeaderFontStyle = headerFormat?.fontStyle?.isItalic ?? false;
  const textColor = headerFormat.textColor ?? DEFAULT_TABLE_HEADER_TEXT_COLOR;
  const backgroundColor = headerFormat.backgroundColor ?? DEFAULT_TABLE_HEADER_BACKGROUND_COLOR;
  const style = {
    id: 'ag-grid-header',
    font: {
      color: textColor,
      bold: isBoldHeaderFontStyle,
      italic: isItalicHeaderFontStyle,
    },
    alignment: {
      wrapText: headerFormat?.wrapHeaders ?? false,
    },
  };
  const interiorStyle = {
    interior: {
      color: backgroundColor,
      pattern: 'Solid',
    },
  };

  if (isEqual(backgroundColor, DEFAULT_TABLE_HEADER_BACKGROUND_COLOR)) {
    return style as IExcelStylesParams;
  }

  return { ...style, ...interiorStyle } as IExcelStylesParams;
};

/** headerClass in columnDef returns an array of class names - ag-grid-header-${alignment} included.
 * We need to use that class name to match the value from columnDef headerClass
 * returns a value with type IExcelStylesParams
 * sample returned value: ```
 * { id: 'ag-grid-header-left', alignment: { horizontal: 'Left' }} ```;
 */
const generateHeaderAlignment = (alignment: string, headerFormat: HeaderFormatStyle) => {
  return {
    id: `ag-grid-header-${alignment}`,
    alignment: {
      horizontal: capitalize(alignment),
      wrapText: headerFormat?.wrapHeaders ?? false,
    },
  } as IExcelStylesParams;
};

const generateCommonCellClasses = (headerFormat: HeaderFormatStyle) => {
  const excelStyles: any = [];
  excelStyles.push(generateHeaderStyle(headerFormat));

  // Grand Total Row
  excelStyles.push({
    id: 'total-row',
    font: {
      bold: true,
    },
  });

  // Group Total Row
  excelStyles.push({
    id: 'subtotal-row',
    font: {
      bold: true,
    },
  });

  // Alignment
  ['Center', 'Left', 'Right'].forEach((alignment) => {
    excelStyles.push({
      id: `${alignment.toLowerCase()}-align`,
      alignment: {
        horizontal: alignment,
      },
    });
  });

  if (headerFormat?.alignHeaders) {
    excelStyles.push(generateHeaderAlignment(headerFormat.alignHeaders, headerFormat));
  }

  if (headerFormat?.alignPivotColumnLabels) {
    excelStyles.push(generateHeaderAlignment(headerFormat.alignPivotColumnLabels, headerFormat));
  }

  return excelStyles;
};

export const getCellClassRules = (params: CellClassParams, rule: string): boolean => {
  const fieldName = getFieldName(params);
  if (!fieldName || params.node.footer) {
    return false;
  }
  const ctx: CustomAgGridContext = params.context;
  return ctx.columnFormats[fieldName]?.format.align === rule;
};

export const generateUrlValue = (value: { url: string; description?: string }) => {
  if (!value) {
    return;
  }
  const linkValue = value.hasOwnProperty('description') ? `${value.description} (${value.url})` : value.url;
  return linkValue;
};

export const unsupportedDateFormats = ['iso_8601_date_time', 'iso_8601_date_time_utc'];
